import React, { useEffect, useMemo, useState } from 'react';
import { authApi, GOOGLE_CLIENT_ID, getApiErrorMessage } from '../utils/api';
import ErrorMessage from './ErrorMessage';

const loadGoogleScript = () => {
    return new Promise((resolve, reject) => {
        if (window.google && window.google.accounts && window.google.accounts.oauth2) {
            resolve();
            return;
        }
        const existing = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
        if (existing) {
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', reject);
            return;
        }
        const script = document.createElement('script');
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = reject;
        document.head.appendChild(script);
    });
};

const LoginModal = ({ isOpen, onClose, onSuccess }) => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [isGoogleReady, setIsGoogleReady] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let mounted = true;
        loadGoogleScript()
            .then(() => {
                if (!mounted) return;
                setIsGoogleReady(true);
            })
            .catch(() => setIsGoogleReady(false));
        return () => {
            mounted = false;
        };
    }, [isOpen]);

    const handleLocalLogin = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;
        setError('');
        setIsSubmitting(true);
        try {
            const data = await authApi.login(email.trim(), password);
            if (data?.accessToken) {
                if (onSuccess) onSuccess(data);
                onClose?.();
            } else {
                setError('Đăng nhập thất bại');
            }
        } catch (err) {
            setError(getApiErrorMessage(err, 'Email hoặc mật khẩu không đúng'));
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleGoogleLogin = async () => {
        if (!isGoogleReady) return;
        try {
            const client = window.google.accounts.oauth2.initCodeClient({
                client_id: GOOGLE_CLIENT_ID,
                scope: 'openid email profile',
                ux_mode: 'popup',
                callback: async (response) => {
                    try {
                        const code = response?.code;
                        if (!code) return;
                        const data = await authApi.socialLogin('google', code, '', { tickerPdf: '' });
                        if (data?.accessToken) {
                            if (onSuccess) onSuccess(data);
                            onClose?.();
                        }
                    } catch (e) {
                        setError(getApiErrorMessage(e, 'Đăng nhập Google thất bại'));
                    }
                }
            });
            client.requestCode();
        } catch (e) {
            setError('Không khởi tạo được Google Login');
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="relative bg-app-bg rounded-xl shadow-2xl w-full max-w-md mx-4" style={{ animation: 'modalZoomPure 0.3s cubic-bezier(0.16, 1, 0.3, 1) forwards' }}>
                <style jsx>{`
                    @keyframes modalZoomPure {
                        from {
                            opacity: 0;
                            transform: scale(0.92);
                        }
                        to {
                            opacity: 1;
                            transform: scale(1);
                        }
                    }
                `}</style>
                <div className="px-6 py-5 relative">
                    <h3 className="text-xl font-semibold text-gray-800">Đăng nhập</h3>
                    <button
                        aria-label="Close"
                        onClick={onClose}
                        className="absolute right-4 top-4 w-8 h-8 rounded-full bg-app-hover hover:bg-app-active text-gray-600"
                    >
                        ×
                    </button>
                </div>
                <div className="px-6 py-5">
                    <form onSubmit={handleLocalLogin} className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Email</label>
                            <input
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                className="mt-1 w-full border border-app-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-blue-500"
                                placeholder="Email hoặc số điện thoại của bạn"
                                required
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Mật khẩu</label>
                            <input
                                type="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="mt-1 w-full border border-app-border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-brand-blue-500"
                                placeholder="Mật khẩu của bạn"
                                required
                            />
                        </div>
                        <ErrorMessage message={error} />
                        <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-2.5 rounded-lg text-white font-medium transition ${isSubmitting ? 'bg-gray-400' : 'bg-brand-blue-500 hover:bg-brand-blue-600'}`}
                        >
                            {isSubmitting ? 'Đang đăng nhập...' : 'Đăng nhập'}
                        </button>
                    </form>

                    <div className="flex items-center my-5">
                        <div className="flex-1 h-px bg-app-border"></div>
                        <span className="px-3 text-xs text-gray-400">HOẶC</span>
                        <div className="flex-1 h-px bg-app-border"></div>
                    </div>

                    <button
                        type="button"
                        onClick={handleGoogleLogin}
                        className="w-full py-2.5 rounded-lg border border-app-border bg-app-bg text-gray-800 font-medium hover:bg-app-hover flex items-center justify-center gap-2"
                    >
                        <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg" alt="Google" className="w-5 h-5" />
                        Đăng nhập với Google
                    </button>
                </div>
                {/* Footer removed as per new design */}
            </div>
        </div>
    );
};

export default LoginModal;


